import { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { Appearance } from '@stripe/stripe-js';
import { XMarkIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { getStripe } from '../lib/stripe';

/**
 * ARGOS-styled in-app checkout (Stripe Payment Element). The card fields are
 * still Stripe-hosted iframes — card data never touches our servers (PCI
 * SAQ A) — we only theme them via the Appearance API to match the dark-glass
 * + cyan look. On success Stripe redirects to `returnUrl` (carrying the
 * payment_intent id), where confirm-on-return + the webhook activate the org.
 */
const appearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#22d3ee',
    colorBackground: '#0d0d10',
    colorText: '#f5f5f5',
    colorTextSecondary: '#9ca3af',
    colorTextPlaceholder: '#6b7280',
    colorDanger: '#f87171',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '8px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      backgroundColor: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.10)',
      boxShadow: 'none',
      color: '#f5f5f5',
    },
    '.Input:focus': {
      border: '1px solid rgba(34,211,238,0.5)',
      boxShadow: '0 0 0 3px rgba(34,211,238,0.12), 0 0 24px -6px rgba(34,211,238,0.4)',
    },
    '.Input::placeholder': { color: '#6b7280' },
    '.Label': { color: '#9ca3af', fontWeight: '500' },
    '.Tab': {
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
    },
    '.Tab:hover': { color: '#22d3ee' },
    '.Tab--selected': {
      backgroundColor: 'rgba(34,211,238,0.10)',
      border: '1px solid rgba(34,211,238,0.5)',
      boxShadow: '0 0 0 1px rgba(34,211,238,0.4)',
    },
    '.Block': {
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
    },
  },
};

function CheckoutForm({ returnUrl }: { returnUrl: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });
    // We only get here on an immediate error; success redirects to returnUrl.
    if (error) {
      setError(error.message || 'Payment failed. Please check your details and try again.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300 tracking-wide">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="group relative w-full py-3.5 rounded-lg overflow-hidden border border-cyan-400/40 bg-cyan-500/15 backdrop-blur-md text-cyan-100 text-xs font-semibold tracking-[0.35em] font-orbitron shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1),0_0_28px_-6px_rgba(34,211,238,0.55)] hover:bg-cyan-500/25 hover:border-cyan-400/70 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15),0_0_36px_-4px_rgba(34,211,238,0.8)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        <span className="relative z-10 flex items-center justify-center gap-2">
          <LockClosedIcon className="h-4 w-4" />
          {submitting ? 'PROCESSING…' : 'PAY SECURELY'}
        </span>
      </button>
      <p className="text-[10px] text-mono-500 text-center tracking-wide">
        Secured by Stripe · your card details never touch our servers
      </p>
    </form>
  );
}

function formatPrice(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function ArgosCheckout({
  clientSecret,
  returnUrl,
  onClose,
  title = 'Complete Payment',
  label,
  amount,
  currency = 'usd',
  interval,
}: {
  clientSecret: string;
  returnUrl: string;
  onClose?: () => void;
  title?: string;
  /** Line-item label, e.g. "Phreatic Onboarding". */
  label?: string;
  /** Price in cents, for the order summary. */
  amount?: number;
  currency?: string;
  /** Billing interval, e.g. "year" → renders "/yr". */
  interval?: string | null;
}) {
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md my-8">
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close checkout"
            className="absolute -top-3 -right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-mono-600 bg-mono-900 text-mono-300 hover:text-white hover:border-cyan-400/60 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <div className="rounded-xl border border-cyan-500/30 bg-mono-900/80 backdrop-blur-xl p-6 shadow-[0_0_60px_-15px_rgba(34,211,238,0.45),inset_0_1px_0_0_rgba(255,255,255,0.06)]">
          <h2 className="text-sm font-bold text-mono-100 tracking-[0.3em] font-orbitron mb-5">
            {title.toUpperCase()}
          </h2>

          {/* Order summary — line item + price */}
          {typeof amount === 'number' && (
            <div className="flex items-baseline justify-between border-b border-mono-700/60 pb-4 mb-5">
              <span className="text-sm text-mono-300">{label || 'Total'}</span>
              <span className="text-lg font-semibold text-cyan-300">
                {formatPrice(amount, currency)}
                {interval && <span className="text-xs text-mono-500 font-normal">/{interval === 'year' ? 'yr' : interval}</span>}
              </span>
            </div>
          )}

          <Elements stripe={getStripe()} options={{ clientSecret, appearance }}>
            <CheckoutForm returnUrl={returnUrl} />
          </Elements>
        </div>
      </div>
    </div>
  );
}
